import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { P_LOGO_URL } from "../../lib/api";
import { useContent } from "../../context/ContentContext";

function LangToggle({ className = "" }) {
  const { locale, setLocale, locales } = useContent();
  if (!locales || locales.length < 2) return null;
  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid="lang-toggle">
      {locales.map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <span className="text-peca-line">/</span>}
          <button
            type="button"
            onClick={() => setLocale(l)}
            data-testid={`lang-${l}`}
            className={`label-mono transition-colors ${locale === l ? "text-peca-yellow" : "text-peca-grey hover:text-peca-cream"}`}
          >
            {l.toUpperCase()}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Nav({ content, solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = content?.nav || {};
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Resolve a nav href to an element usable from the current route.
  const NavItem = ({ l, className, onClick }) => {
    const raw = (l.href || "").trim();
    if (raw.startsWith("#")) {
      const resolved = onHome ? raw : `/${raw}`;
      return <a href={resolved} data-testid={`nav-link-${l.label}`} className={className} onClick={onClick}>{l.label}</a>;
    }
    if (/^https?:\/\//i.test(raw)) {
      return <a href={raw} target="_blank" rel="noopener noreferrer" data-testid={`nav-link-${l.label}`} className={className} onClick={onClick}>{l.label}</a>;
    }
    const to = raw.startsWith("/") ? raw : `/${raw}`;
    return <Link to={to} data-testid={`nav-link-${l.label}`} className={className} onClick={onClick}>{l.label}</Link>;
  };

  const ctaHref = onHome ? "#contacto" : "/#contacto";
  const solidBar = scrolled || solid;

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solidBar ? "bg-peca-black/90 backdrop-blur-md border-b border-peca-line" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={P_LOGO_URL} alt="Peça" className="h-10 w-10 object-contain transition-transform duration-300 group-hover:-translate-y-0.5" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-extrabold uppercase tracking-tight text-lg text-peca-cream">Peça</span>
            <span className="font-mono uppercase tracking-[0.2em] text-[9px] text-peca-grey mt-1">Business Solutions</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {(nav.links || []).map((l, i) => (
            <NavItem key={`${l.href}-${i}`} l={l} className="label-mono text-peca-grey hover:text-peca-yellow transition-colors duration-200" />
          ))}
          {(content?.pages || []).filter((p) => p.showInNav !== false).map((p) => (
            <Link key={p.id} to={`/${p.slug}`} data-testid={`nav-link-${p.title}`} className="label-mono text-peca-grey hover:text-peca-yellow transition-colors duration-200">
              {p.title}
            </Link>
          ))}
          <LangToggle className="ml-1" />
          <a href={ctaHref} data-testid="nav-cta" className="label-mono bg-peca-yellow text-peca-black px-5 py-3 hover:bg-peca-cream transition-colors duration-200">
            {nav.cta || "Contacto"}
          </a>
        </nav>

        <button className="md:hidden text-peca-cream" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-peca-black border-t border-peca-line px-5 py-6 flex flex-col gap-5">
          {(nav.links || []).map((l, i) => (
            <NavItem key={`${l.href}-${i}`} l={l} onClick={() => setOpen(false)} className="label-mono text-peca-cream" />
          ))}
          {(content?.pages || []).filter((p) => p.showInNav !== false).map((p) => (
            <Link key={p.id} to={`/${p.slug}`} onClick={() => setOpen(false)} data-testid={`nav-link-mobile-${p.title}`} className="label-mono text-peca-cream">
              {p.title}
            </Link>
          ))}
          <a href={ctaHref} onClick={() => setOpen(false)} data-testid="nav-cta-mobile" className="label-mono bg-peca-yellow text-peca-black px-5 py-3 w-fit">
            {nav.cta || "Contacto"}
          </a>
          <LangToggle className="pt-2" />
        </div>
      )}
    </header>
  );
}
